import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, MapPin, IndianRupee, Plus, Clock, X, Stethoscope, Users, MessageCircle, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/lib/config";
import { track } from "@/lib/track";
import AppHeader from "@/components/AppHeader";
import { SHIFT_STATUS_STYLES, waLink, rupees } from "@/lib/utils-lc";
import RatingStars from "@/components/RatingStars";
import RateDialog from "@/components/RateDialog";
import CancelDialog from "@/components/CancelDialog";
import { Star, XCircle } from "lucide-react";

const SPECIALTIES = ["General Physician", "Anaesthesia", "Paediatrics", "Emergency", "Cardiology", "Orthopaedics", "OBG", "Radiology", "ICU / Critical Care", "Nurse — General Ward", "Nurse — ICU", "Nurse — OT", "Clinical Assistant", "Alternative Medicine"];

export default function FacilityPage() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [activeShift, setActiveShift] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts/mine");
      setShifts(data);
    } catch (err) { console.error("load facility shifts failed:", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <AppHeader subtitle="Facility workspace" />
      <div className="max-w-5xl mx-auto px-5 py-8 md:py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{user?.profile?.facility_name}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Posted shifts</h1>
            <p className="text-slate-600 mt-1 text-sm">Verified professionals see your shifts and apply. Tap a shift to review applicants, confirm one, and settle payment.</p>
          </div>
          <Button data-testid="open-post-shift-btn" onClick={() => setCreating(true)} className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-11 px-5">
            <Plus className="w-4 h-4 mr-1.5" /> Post a shift
          </Button>
        </div>

        {shifts.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-slate-300 p-10 text-center bg-white">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 flex items-center justify-center mx-auto"><CalendarDays className="w-5 h-5" /></div>
            <div className="font-display text-lg font-semibold mt-4">No shifts yet</div>
            <p className="text-sm text-slate-500 mt-1">Post your first shift to see it here.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {shifts.map((s) => <ShiftCard key={s.shift_id} shift={s} onOpen={() => setActiveShift(s)} />)}
          </div>
        )}
      </div>

      {creating && <PostShiftModal onClose={() => setCreating(false)} onCreated={async () => { setCreating(false); await load(); }} />}
      {activeShift && (
        <ShiftDetailDrawer
          shiftId={activeShift.shift_id}
          facilityPhone={user?.profile?.phone}
          onClose={() => setActiveShift(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ShiftCard({ shift, onOpen }) {
  const cls = SHIFT_STATUS_STYLES[shift.status] || "bg-slate-100 text-slate-700";
  return (
    <button type="button" onClick={onOpen} className="text-left" data-testid={`facility-shift-card-${shift.shift_id}`}>
      <Card className="rounded-2xl border-slate-200 p-5 bg-white hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-emerald-800">
              <Stethoscope className="w-3 h-3" /> {shift.specialty}
            </div>
            <div className="font-display text-lg font-semibold mt-1">{shift.date}</div>
            <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{shift.start_time} — {shift.end_time}</div>
          </div>
          <Badge className={cls + " hover:" + cls}>{shift.status}</Badge>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5 text-slate-600"><MapPin className="w-3.5 h-3.5" />{shift.location}</div>
          <div className="flex items-center gap-1 font-display font-bold text-slate-900"><IndianRupee className="w-3.5 h-3.5" />{Number(shift.pay_inr).toLocaleString("en-IN")}</div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
          <span className="text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {shift.applicant_count || 0} applicant{shift.applicant_count === 1 ? "" : "s"}</span>
          <span className="text-emerald-800 font-medium flex items-center gap-0.5">Manage <ArrowRight className="w-3.5 h-3.5" /></span>
        </div>
      </Card>
    </button>
  );
}

function ShiftDetailDrawer({ shiftId, facilityPhone, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(null); // application_id awaiting payment method
  const [completing, setCompleting] = useState(false);
  const [rating, setRating] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shifts/${shiftId}/applicants`);
      setDetail(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    }
  }, [shiftId]);
  useEffect(() => { load(); }, [load]);

  const doComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/shifts/${shiftId}/complete`);
      track("shift_completed", { shift_id: shiftId });
      toast.success("Shift marked completed. Payment released to the professional.");
      await load();
      await onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete");
    } finally {
      setCompleting(false);
    }
  };

  if (!detail) return (
    <Overlay onClose={onClose}>
      <div className="p-8 text-slate-500 text-sm">Loading…</div>
    </Overlay>
  );

  const s = detail.shift;
  const apps = detail.applications;
  const confirmedApp = apps.find(a => a.status === "confirmed" || a.status === "completed");
  const isOpen = s.status === "open";
  const isConfirmed = s.status === "confirmed";
  const isCompleted = s.status === "completed";
  const canRatePro = isCompleted && confirmedApp && !detail.my_rating_of_professional;

  const wa = confirmedApp ? waLink(confirmedApp.professional_snapshot?.phone, `Hi from ${s.facility_name}, regarding your ${s.specialty} shift on ${s.date}.`) : null;

  return (
    <Overlay onClose={onClose}>
      <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Shift · {s.status}</div>
          <div className="font-display text-lg font-bold">{s.specialty}</div>
        </div>
        <div className="flex items-center gap-2">
          {(s.status === "open" || s.status === "confirmed") && (
            <Button variant="ghost" size="sm" className="text-rose-700 hover:bg-rose-50 rounded-full" onClick={() => setCancelling(true)} data-testid="facility-cancel-shift-btn">
              <XCircle className="w-4 h-4 mr-1" /> Cancel
            </Button>
          )}
          <button onClick={onClose} data-testid="close-shift-detail" className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <Card className="rounded-2xl border-slate-200 p-4 bg-slate-50/50">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Meta label="Date" value={s.date} />
            <Meta label="Time" value={`${s.start_time} — ${s.end_time}`} />
            <Meta label="Location" value={s.location} />
            <Meta label="Pay" value={rupees(s.pay_inr)} />
          </div>
          {s.notes && <div className="mt-3 text-sm text-slate-600 border-t border-slate-200 pt-3">{s.notes}</div>}
        </Card>

        {isConfirmed && confirmedApp && (
          <ConfirmedBlock app={confirmedApp} wa={wa} onComplete={doComplete} completing={completing} />
        )}
        {isCompleted && confirmedApp && (
          <Card className="rounded-2xl border-emerald-200 bg-emerald-50/70 p-4 text-sm text-emerald-900">
            <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Shift completed</div>
            <p className="mt-1 text-emerald-900/80">Payment released to {confirmedApp.professional_snapshot?.name}. 10% platform fee retained.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-medium px-4 py-2"><MessageCircle className="w-4 h-4" /> WhatsApp {confirmedApp.professional_snapshot?.name}</a>}
              {canRatePro && (
                <Button size="sm" onClick={() => setRating(true)} className="rounded-full bg-amber-500 hover:bg-amber-600" data-testid="facility-rate-pro-btn">
                  <Star className="w-3.5 h-3.5 mr-1" /> Rate professional
                </Button>
              )}
              {detail.my_rating_of_professional && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-900/80"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> You rated {detail.my_rating_of_professional.stars}★</span>
              )}
            </div>
          </Card>
        )}

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {apps.length} applicant{apps.length === 1 ? "" : "s"}</div>
          {apps.length === 0 ? (
            <Card className="p-6 text-center text-sm text-slate-500 rounded-2xl border-dashed border-slate-300 bg-white">No applicants yet. Verified professionals will see your shift under Browse.</Card>
          ) : (
            <div className="space-y-3">
              {apps.map(a => (
                <ApplicantRow key={a.application_id} app={a} showConfirm={isOpen} onConfirmClick={() => setConfirming(a)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <ConfirmPaymentModal
          shift={s}
          app={confirming}
          onClose={() => setConfirming(null)}
          onDone={async () => { setConfirming(null); await load(); await onChanged?.(); }}
        />
      )}
      <RateDialog
        open={rating}
        onClose={() => setRating(false)}
        shiftId={s.shift_id}
        rateeName={confirmedApp?.professional_snapshot?.name}
        onDone={async () => { setRating(false); await load(); }}
      />
      <CancelDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        shiftId={s.shift_id}
        onDone={async () => { setCancelling(false); await load(); await onChanged?.(); }}
      />
    </Overlay>
  );
}

function ConfirmedBlock({ app, wa, onComplete, completing }) {
  return (
    <Card className="rounded-2xl border-indigo-200 bg-indigo-50/40 p-5 space-y-3">
      <div className="flex items-center gap-2 text-indigo-900 font-display font-semibold text-lg"><CheckCircle2 className="w-5 h-5 text-indigo-700" /> Confirmed with {app.professional_snapshot?.name}</div>
      <div className="flex flex-wrap items-center gap-2">
        {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid="facility-wa-link" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-medium px-4 py-2"><MessageCircle className="w-4 h-4" /> WhatsApp professional</a>}
        <Button size="sm" onClick={onComplete} disabled={completing} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="mark-completed-btn">
          {completing ? "Completing…" : "Mark shift completed"}
        </Button>
      </div>
    </Card>
  );
}

function ApplicantRow({ app, showConfirm, onConfirmClick }) {
  const p = app.professional_snapshot || {};
  const status = app.status;
  const badge = {
    applied: "bg-slate-100 text-slate-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    declined: "bg-slate-100 text-slate-500",
    completed: "bg-indigo-100 text-indigo-800",
  }[status] || "bg-slate-100 text-slate-800";
  return (
    <Card className="p-4 rounded-2xl border-slate-200 bg-white" data-testid={`applicant-row-${app.application_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold">{p.name}</span>
            {p.credential_status === "verified" && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
            <Badge className={badge + " hover:" + badge + " text-[10px]"}>{status}</Badge>
          </div>
          {app.professional_rating && <RatingStars rating={app.professional_rating} className="mt-1" />}
          <div className="text-xs text-slate-600 mt-1">{p.professional_type} · {p.specialty}{p.degree ? ` · ${p.degree}` : ""} · {p.years_experience || 0} yrs · {rupees(p.rate_inr_per_hour || 0)}/hr</div>
        </div>
        {showConfirm && (
          <Button size="sm" onClick={onConfirmClick} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid={`confirm-applicant-${app.application_id}`}>
            Confirm
          </Button>
        )}
      </div>
    </Card>
  );
}

function ConfirmPaymentModal({ shift, app, onClose, onDone }) {
  const cfg = useConfig();
  const [method, setMethod] = useState("mock_escrow");
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);
  const amount = Number(shift.pay_inr);
  const commission = Math.round(amount * 0.10);
  const net = amount - commission;

  const openRazorpay = (order) => new Promise((resolve) => {
    if (!window.Razorpay) return resolve({ error: "Razorpay script not loaded" });
    const rzp = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount_paise,
      currency: order.currency,
      name: "LocumConnect",
      description: `${shift.specialty} · ${shift.date}`,
      order_id: order.razorpay_order_id,
      handler: (res) => resolve({ ok: true, res }),
      modal: { ondismiss: () => resolve({ dismissed: true }) },
      theme: { color: "#047857" },
    });
    rzp.open();
  });

  const submit = async () => {
    if (method === "upi" && !upi.includes("@")) return toast.error("Enter a valid UPI ID (e.g., name@bank)");
    setBusy(true);
    try {
      const { data } = await api.post(`/shifts/${shift.shift_id}/confirm`, {
        application_id: app.application_id,
        payment_method: method,
        upi_id: method === "upi" ? upi : null,
      });
      if (data.needs_payment && method === "razorpay") {
        const result = await openRazorpay(data);
        if (result.dismissed) { toast.error("Payment cancelled"); return; }
        if (result.error) { toast.error(result.error); return; }
        await api.post(`/shifts/${shift.shift_id}/confirm/verify`, {
          application_id: app.application_id,
          razorpay_order_id: result.res.razorpay_order_id,
          razorpay_payment_id: result.res.razorpay_payment_id,
          razorpay_signature: result.res.razorpay_signature,
        });
      }
      toast.success("Applicant confirmed · other applicants declined");
      track("shift_confirmed", { shift_id: shift.shift_id, method });
      await onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Confirm & pay</div>
            <div className="font-display text-lg font-bold">Confirm {app.professional_snapshot?.name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="close-confirm-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <Card className="rounded-2xl border-slate-200 bg-slate-50/50 p-4">
            <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
              <div>Shift pay<div className="text-slate-800 font-semibold text-sm mt-0.5">{rupees(amount)}</div></div>
              <div>Platform fee (10%)<div className="text-slate-800 font-semibold text-sm mt-0.5">−{rupees(commission)}</div></div>
              <div>Professional receives<div className="text-emerald-800 font-semibold text-sm mt-0.5">{rupees(net)}</div></div>
            </div>
          </Card>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 block">Payment method</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="space-y-2">
              {cfg.razorpay_enabled && (
                <PaymentOption id="pay-razorpay" testid="pay-method-razorpay" value="razorpay" title="Razorpay (card / UPI / netbanking)" desc="Real payment held with Razorpay; released to professional on completion." />
              )}
              <PaymentOption id="pay-escrow" testid="pay-method-escrow" value="mock_escrow" title="Escrow (mocked)" desc="We hold the amount and release after the shift is completed." />
              <PaymentOption id="pay-upi" testid="pay-method-upi" value="upi" title="UPI transfer" desc="Enter your UPI ID; the professional will collect after completion." />
              <PaymentOption id="pay-cash" testid="pay-method-cash" value="cash" title="Cash on shift" desc="Handover in person at the facility on the shift day." />
            </RadioGroup>
          </div>

          {method === "upi" && (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Your UPI ID</Label>
              <Input data-testid="pay-upi-input" value={upi} onChange={e => setUpi(e.target.value)} placeholder="name@bank" className="h-11 rounded-xl" />
            </div>
          )}

          <div className="text-xs text-slate-500 leading-relaxed">By confirming, all other applicants for this shift will be automatically declined.</div>

          <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="submit-confirm-btn">
            {busy ? "Confirming…" : method === "razorpay" ? "Confirm and pay with Razorpay" : `Confirm and ${method === "cash" ? "handover on shift" : "hold payment"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PaymentOption({ id, testid, value, title, desc }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 border border-slate-200 rounded-xl p-3 cursor-pointer hover:border-emerald-500 has-[:checked]:border-emerald-500 has-[:checked]:bg-emerald-50/40">
      <RadioGroupItem id={id} value={value} className="mt-0.5" data-testid={testid} />
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="text-slate-800 font-medium">{value}</div>
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function PostShiftModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const cfg = useConfig();
  const defaultCity = user?.profile?.city || cfg.supported_cities?.[0] || "Pune";
  const [f, setF] = useState({ specialty: "", date: "", start_time: "", end_time: "", pay_inr: "", location: "", notes: "", city: defaultCity });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.specialty || !f.date || !f.start_time || !f.end_time || !f.pay_inr || !f.location) return toast.error("Fill all required fields");
    setBusy(true);
    try {
      await api.post("/shifts", { ...f, pay_inr: Number(f.pay_inr) });
      track("shift_posted", { city: f.city, specialty: f.specialty, pay_inr: Number(f.pay_inr) });
      toast.success("Shift posted");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post shift");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-t-3xl md:rounded-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">New shift</div>
            <div className="font-display text-lg font-bold">Post a shift</div>
          </div>
          <button onClick={onClose} data-testid="close-post-shift" className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <Field label="Specialty *">
            <Select value={f.specialty} onValueChange={(v) => setF({...f, specialty: v})}>
              <SelectTrigger data-testid="shift-specialty" className="h-11 rounded-xl"><SelectValue placeholder="Choose specialty" /></SelectTrigger>
              <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date *"><Input data-testid="shift-date" type="date" value={f.date} onChange={e => setF({...f, date: e.target.value})} className="h-11 rounded-xl" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start time *"><Input data-testid="shift-start" type="time" value={f.start_time} onChange={e => setF({...f, start_time: e.target.value})} className="h-11 rounded-xl" /></Field>
            <Field label="End time *"><Input data-testid="shift-end" type="time" value={f.end_time} onChange={e => setF({...f, end_time: e.target.value})} className="h-11 rounded-xl" /></Field>
          </div>
          <Field label="Pay offered (₹) *"><Input data-testid="shift-pay" type="number" value={f.pay_inr} onChange={e => setF({...f, pay_inr: e.target.value})} placeholder="e.g. 4500" className="h-11 rounded-xl" /></Field>
          <Field label="City *">
            <Select value={f.city} onValueChange={(v) => setF({...f, city: v})}>
              <SelectTrigger data-testid="shift-city" className="h-11 rounded-xl"><SelectValue placeholder="Choose city" /></SelectTrigger>
              <SelectContent>{cfg.supported_cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Location (address) *"><Input data-testid="shift-location" value={f.location} onChange={e => setF({...f, location: e.target.value})} placeholder="e.g. Koregaon Park" className="h-11 rounded-xl" /></Field>
          <Field label="Notes (optional)"><Textarea data-testid="shift-notes" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} rows={3} placeholder="Any specific requirements" className="rounded-xl" /></Field>
          <Button type="submit" data-testid="shift-submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800">
            {busy ? "Posting…" : "Post shift"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
